import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import EAModal from "../../../shared/Modal/EAModal";
import EAButton from "../../../shared/Buttons/EAButton";
import EAQuantitySelector from "../../../shared/QuantitySelector/EAQuantitySelector";
import EAAlert from "../../../shared/EAAlert";
import { Exercise } from "../../../../models/Exercise";
import { Assort } from "../../../../models/Assort";
import { SaleStep } from "../../../../store/stepSaleConfig/stepSaleConfigReducer";
import { BasketItem, setBasketItemExercise } from "../../../../models/BasketItem";
import { setTicketAccessFromExercise } from "../../../../models/TicketAccess";
import { setAddToBasketMessage } from "../../../../store/alerts/alertsActions";
import useBatchAddToBasket from "../../../Universal/PreBasketSummary/hooks/useBatchAddToBasket";
import useApiRequest from "../../../../../utils/hooks/useApiRequest";
import { GET_BASKET_URL } from "../../../../api/apiRoutes";
import { FINALIZE_PAGE } from "../../../../routes";
import { CHECKOUT_STEP_KIND_PARAM } from "../../../../checkout/checkoutStepKindConfig";
import getId from "../../../../../utils/getId";
import "./RecruitmentConfirmModal.scss";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  item: Exercise;
  tickets: Assort[];
  saleStep: SaleStep;
  facilityIds: string;
}

const RecruitmentConfirmModal: React.FC<IProps> = ({ isOpen, onClose, item, tickets, saleStep, facilityIds }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { addToBasket } = useBatchAddToBasket();
  const { get } = useApiRequest();

  const firstTicket = tickets[0];
  const availableSeats = item.capacity - item.reserved;
  const maxQnt = firstTicket?.maxSale > 0 ? Math.min(firstTicket.maxSale, availableSeats) : availableSeats;

  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState("");

  const buildBasketItem = (qnt: number): BasketItem => {
    const access = setTicketAccessFromExercise(item.objectId, item.name, item.startTime);
    return setBasketItemExercise(facilityIds, getId(firstTicket), firstTicket.price, [access], null, qnt);
  };

  const navigateToFinalize = (hasSurvey: boolean) => {
    const params = new URLSearchParams(window.location.search);
    params.set(CHECKOUT_STEP_KIND_PARAM, saleStep.kind);
    params.set("step", hasSurvey ? "2" : "1");
    navigate(`${FINALIZE_PAGE}?${params.toString()}`);
  };

  const handleAddAndProceed = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setAlert("");

    const result = await addToBasket(buildBasketItem(quantity));

    if (result.isError && result.errorTable.length > 0) {
      setAlert(result.errorTable[0]);
      setIsLoading(false);
      return;
    }

    const { result: basketResult, isError } = await get(GET_BASKET_URL);
    setIsLoading(false);

    if (isError || !basketResult?.basket) {
      navigateToFinalize(false);
      return;
    }

    const hasSurvey = basketResult.basket.some((bi: BasketItem) => bi.hasSurvey);
    navigateToFinalize(hasSurvey);
  };

  const handleAddOnly = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setAlert("");

    const result = await addToBasket(buildBasketItem(quantity));

    setIsLoading(false);

    if (result.isError && result.errorTable.length > 0) {
      setAlert(result.errorTable[0]);
      return;
    }

    dispatch(setAddToBasketMessage(t("dodano_do_koszyka")));
    onClose();
  };

  const handleClose = () => {
    if (isLoading) return;
    setAlert("");
    onClose();
  };

  return (
    <EAModal
      isOpen={isOpen}
      onClose={handleClose}
      title={item.name}
      hideButtons
      desktopWidth="460px"
    >
      <div className="recruitment-modal">
        <p className="recruitment-modal__description">{t("nabor_potwierdzenie_opis")}</p>

        <div className="recruitment-modal__quantity-row">
          <span className="recruitment-modal__quantity-label">{t("liczba_uczestnikow")}</span>
          <EAQuantitySelector
            value={quantity}
            onChange={setQuantity}
            min={1}
            max={maxQnt}
            disabled={isLoading}
          />
        </div>

        {alert && <EAAlert type="error" message={alert} />}

        <div className="recruitment-modal__footer">
          <EAButton
            variant="outlined"
            title={t("dodaj_do_koszyka")}
            onClick={handleAddOnly}
            loading={isLoading}
            fullWidth
          />
          <EAButton
            variant="filled"
            title={saleStep.buttonCustomTitle || t("zloz_rezerwacje")}
            onClick={handleAddAndProceed}
            loading={isLoading}
            fullWidth
          />
        </div>
      </div>
    </EAModal>
  );
};

export default RecruitmentConfirmModal;
